import React, { useState } from 'react';
import { Quote, Sparkles, MapPin, Heart, Check, Save } from 'lucide-react';
import { CoupleSettings } from '../../types';

interface PhrasesAdminSectionProps {
  settings: CoupleSettings;
  onSaveSettings: (newSettings: CoupleSettings) => void;
  showCardWrapper?: boolean;
}

const PRESET_QUOTES = [
  'Eres mi hoy y todos mis mañanas ❤️',
  'El mejor lugar del mundo es a tu lado ✨',
  'Cada día a tu lado es mi recuerdo favorito 🌸',
  'No te amo por lo que eres, sino por lo que soy cuando estoy contigo 💫',
  'Tu sonrisa es mi lugar favorito en todo el universo 🌷',
  'Juntos es mi lugar favorito para siempre 💕',
];

export const PhrasesAdminSection: React.FC<PhrasesAdminSectionProps> = ({
  settings,
  onSaveSettings,
  showCardWrapper = true,
}) => {
  const [quote, setQuote] = useState(settings.quote || '');
  const [location, setLocation] = useState(settings.location || '');
  const [p1Title, setP1Title] = useState(settings.partner1Title || '');
  const [p2Title, setP2Title] = useState(settings.partner2Title || '');
  const [savedSuccess, setSavedSuccess] = useState(false);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onSaveSettings({
      ...settings,
      quote: quote.trim(),
      location: location.trim(),
      partner1Title: p1Title.trim(),
      partner2Title: p2Title.trim(),
    });
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 2500);
  };

  const content = (
    <div className="space-y-6">
      {/* Live Preview Card */}
      <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-rose-500/10 via-amber-500/5 to-purple-500/10 border border-rose-500/20 relative overflow-hidden">
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-[11px] font-bold text-rose-600 uppercase tracking-wider flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Vista Previa en Vivo</span>
          </span>
          <span className="text-[10px] text-slate-500 font-medium">Así se verá en la web pública</span>
        </div>
        <blockquote className="font-['Playfair_Display',serif] text-base sm:text-lg italic text-slate-800 leading-relaxed text-center py-2">
          "{quote || 'Escribe una frase de amor...'}"
        </blockquote>
        <div className="flex items-center justify-center gap-3 text-xs text-slate-500 mt-2 font-medium">
          {location && (
            <span className="flex items-center gap-1 text-slate-600">
              <MapPin className="w-3.5 h-3.5 text-rose-500" />
              <span>{location}</span>
            </span>
          )}
          <span>•</span>
          <span className="flex items-center gap-1 font-bold text-slate-700">
            <Heart className="w-3.5 h-3.5 text-rose-500 fill-rose-500" />
            <span>{settings.partner1Name || 'Novio'} & {settings.partner2Name || 'Novia'}</span>
          </span>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-5">
        {/* Main Quote */}
        <div>
          <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Quote className="w-3.5 h-3.5 text-rose-500" />
              <span>Frase Principal de Portada</span>
            </span>
            <span className="text-[10px] text-slate-400 font-normal">Visible en la barra principal</span>
          </label>
          <textarea
            rows={2}
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            placeholder="Ej. Eres el motivo de mis sonrisas cada mañana..."
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 focus:bg-white transition-all placeholder:text-slate-400 resize-none font-medium"
          />

          {/* Quick Presets */}
          <div className="mt-2.5">
            <span className="text-[11px] font-semibold text-slate-500 block mb-1.5">Sugerencias rápidas:</span>
            <div className="flex flex-wrap gap-1.5">
              {PRESET_QUOTES.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setQuote(preset)}
                  className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-rose-50 hover:text-rose-700 text-slate-600 border border-slate-200/80 transition-all cursor-pointer font-medium text-left"
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Location & Titles Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 pt-2">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-amber-500" />
              <span>Lugar / Ciudad Especial</span>
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ej. Bogotá, Colombia"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-xs focus:outline-none focus:ring-2 focus:ring-rose-400 focus:bg-white transition-all placeholder:text-slate-400"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Apodo Romántico ({settings.partner1Name || 'Novio'})
            </label>
            <input
              type="text"
              value={p1Title}
              onChange={(e) => setP1Title(e.target.value)}
              placeholder="Ej. Mi Amor, El Novio..."
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-xs focus:outline-none focus:ring-2 focus:ring-rose-400 focus:bg-white transition-all placeholder:text-slate-400"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Apodo Romántico ({settings.partner2Name || 'Novia'})
            </label>
            <input
              type="text"
              value={p2Title}
              onChange={(e) => setP2Title(e.target.value)}
              placeholder="Ej. Mi Niña, Mi Princesa..."
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-xs focus:outline-none focus:ring-2 focus:ring-rose-400 focus:bg-white transition-all placeholder:text-slate-400"
            />
          </div>
        </div>

        {/* Submit Bar */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
          {savedSuccess && (
            <span className="text-xs font-bold text-emerald-600 flex items-center gap-1.5 animate-fadeIn">
              <Check className="w-4 h-4" />
              <span>Frase actualizada exitosamente</span>
            </span>
          )}
          <button
            type="submit"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-all shadow-xs cursor-pointer active:scale-95"
          >
            <Save className="w-3.5 h-3.5" />
            <span>Guardar Frases</span>
          </button>
        </div>
      </form>
    </div>
  );

  if (!showCardWrapper) {
    return content;
  }

  return (
    <div className="bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-7 shadow-xs">
      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-slate-100">
        <div className="w-10 h-10 rounded-2xl bg-rose-50 border border-rose-200 text-rose-600 flex items-center justify-center font-bold">
          <Quote className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-bold text-base text-slate-900">Frases & Dedicatorias Románticas</h3>
          <p className="text-xs text-slate-500">Personaliza la frase inspiradora y dedicatorias de tu espacio</p>
        </div>
      </div>
      {content}
    </div>
  );
};
